//  ----------      START   IMPORT  BLOCK       ----------
import { useState, useMemo, useCallback } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Accordion from 'react-bootstrap/Accordion';

// Widgets
import YearlyDisplay from '../../Widgets/Professional/SummaryForm/TagDisplay/YearlyDisplay';
import StatusDisplay from '../../Widgets/Professional/SummaryForm/StatusDisplay/StatusDisplay';
import { AllergiesWidget } from '../../Widgets/Professional/SummaryForm/Allergies/AllergiesWidget';
import { HereditaryWidget } from '../../Widgets/Professional/SummaryForm/Hereditary/HereditaryWidget';
import GeneralDisplay from '../../Widgets/Professional/SummaryForm/GeneralDisplay/GeneralDisplay';
import SexDisplay from '../../Widgets/Professional/SummaryForm/SexDisplay/SexDisplay';
import ClinicHistoryDisplay from '../../Widgets/Professional/SummaryForm/ClinicHistoryDisplay/ClinicHistoryDisplay';
import DefaulSummary from '../../Widgets/LoggedUserGeneral/DefaultSummary/DefaultSummary';

// Utils & Context
import { calculateAge } from '../../../Utils/dateUtils';
import { useLanguage } from '../../../API/Context/LanguageContext';
import { useActivePatient } from '../../../API/Hooks/ActivePatientContext';

// Config & Multilingual
import { BLOOD_TYPES } from '../../../ConfigFiles/maps/blood_types_map';
import { VocabSummary as smry_vocab } from '../../../ConfigFiles/multilingual/VocabSummary';
//  ----------      END   IMPORT  BLOCK       ----------

const SummaryForm = () => {
  const { lan } = useLanguage();
  const { activePatient, isLoadingActivePatient } = useActivePatient();

  // Extract core patient demographic object
  const patient = activePatient?.patient;

  // Controlled active accordion key
  const [activeAccordionKey, setActiveAccordionKey] = useState([]);

  // Extract diagnoses & contained affections
  const diagnoses = useMemo(() => {
    return Array.isArray(activePatient?.diagnoses?.diagnoses) ? activePatient.diagnoses.diagnoses : [];
  }, [activePatient]);

  const containedAffections = useMemo(() => {
    return Array.isArray(activePatient?.diagnoses?.contained_affections) ? activePatient.diagnoses.contained_affections : [];
  }, [activePatient]);

  const prescriptions = useMemo(() => {
    if (Array.isArray(activePatient?.prescriptions)) {
      return activePatient.prescriptions;
    }
    return diagnoses
      .filter((d) => d?.prescription)
      .map((d) => ({
        diagnosis_id: d._id,
        raw_text: d.prescription.raw_text,
        ...d.prescription,
      }));
  }, [activePatient, diagnoses]);

  // Resolve Blood Type label from map
  const displayBloodType = useMemo(() => {
    if (patient?.blood_type === undefined || patient?.blood_type === null) {
      return 'N/A';
    }
    const bloodTypeObj = BLOOD_TYPES.find((bt) => bt.id === patient.blood_type);
    return bloodTypeObj ? bloodTypeObj.labels[lan] : 'N/A';
  }, [patient?.blood_type, lan]);

  // Derived full name calculation
  const fullName = useMemo(() => {
    if (!patient) return '—';
    const nameParts = [patient.name, patient.lastname_1, patient.lastname_2].filter(Boolean);
    return nameParts.join(' ') || '—';
  }, [patient]);

  // Derived age calculation
  const age = useMemo(() => {
    return patient?.birthday ? calculateAge(patient.birthday) : '—';
  }, [patient?.birthday]);

  // Inter-widget interaction state
  const [selectedEntryId, setSelectedEntryId] = useState(null);

  const handleOpenInHistory = useCallback((_id) => {
    setSelectedEntryId(_id);
    // Expand clinic history accordion section if collapsed
    setActiveAccordionKey((prevKeys) => {
      const keys = Array.isArray(prevKeys) ? prevKeys : [prevKeys];
      return keys.includes('clinic_history') ? keys : [...keys, 'clinic_history'];
    });
  }, []);

  if (isLoadingActivePatient) {
    return (
      <Container className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading patient data...</span>
        </div>
      </Container>
    );
  }

  if (!activePatient || !patient) {
    return <DefaulSummary />;
  }

  return (
    <Container className="my-3">
      <Row>
        <Col xs={10}>
          {/* Header Row */}
          <Row className="align-items-center mb-3">
            <Col xs={8}>
              <h2 className="m-0">{fullName}</h2>
              <p className="text-muted mb-0">{age} {lan === 0 ? 'años' : 'years'}</p>
            </Col>
            <Col xs={2}>
              <SexDisplay />
            </Col>
            <Col xs={2} className="text-end">
              <h1 className="m-0 text-primary">{displayBloodType}</h1>
            </Col>
          </Row>

          {/* Master Accordion Container */}
          <Accordion
            activeKey={activeAccordionKey}
            onSelect={(keys) => setActiveAccordionKey(keys)}
            alwaysOpen
            flush
            className="shadow-sm rounded border"
          >
            {/* 1. General Information */}
            <Accordion.Item eventKey="general">
              <Accordion.Header>{smry_vocab.general[lan]}</Accordion.Header>
              <Accordion.Body>
                <GeneralDisplay />
              </Accordion.Body>
            </Accordion.Item>

            {/* 2. Allergies */}
            <Accordion.Item eventKey="allergies">
              <Accordion.Header>
                <i className="bi bi-shield-exclamation text-danger me-2" />
                {lan === 0 ? 'Alergias' : 'Allergies'}
              </Accordion.Header>
              <Accordion.Body>
                <AllergiesWidget />
              </Accordion.Body>
            </Accordion.Item>

            {/* 3. Hereditary Conditions */}
            <Accordion.Item eventKey="hereditary">
              <Accordion.Header>
                <i className="bi bi-diagram-3 text-primary me-2" />
                {lan === 0 ? 'Antecedentes Heredofamiliares' : 'Hereditary History'}
              </Accordion.Header>
              <Accordion.Body>
                <HereditaryWidget />
              </Accordion.Body>
            </Accordion.Item>

            {/* 4. Pathologic History (Yearly Tags) */}
            <Accordion.Item eventKey="pathologic_history">
              <Accordion.Header>{smry_vocab.pathologic_history[lan]}</Accordion.Header>
              <Accordion.Body>
                <YearlyDisplay
                  diagnoses={diagnoses}
                  containedAffections={containedAffections}
                  handleOpenInHistory={handleOpenInHistory}
                />
              </Accordion.Body>
            </Accordion.Item>

            {/* 5. Notes & Clinical History */}
            <Accordion.Item eventKey="clinic_history">
              <Accordion.Header>{smry_vocab.notes_history[lan]}</Accordion.Header>
              <Accordion.Body>
                <ClinicHistoryDisplay
                  diagnoses={diagnoses}
                  prescriptions={prescriptions}
                  requested_id={selectedEntryId}
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Col>

        <Col xs={2}>
          <StatusDisplay />
        </Col>
      </Row>
    </Container>
  );
};

export default SummaryForm;